/** Place types (Places API) that count as grocery. */
export const GROCERY_PLACE_TYPES = new Set([
  'grocery_store',
  'supermarket',
  'convenience_store',
  'food_store',
  'discount_supermarket',
  'farmers_market',
  'health_food_store',
  'market',
]);

/** Types that indicate a place is primarily a restaurant/cafe, not a grocery store. */
export const RESTAURANT_LIKE_TYPES = new Set([
  'cafe',
  'coffee_shop',
  'sandwich_shop',
  'restaurant',
  'bakery',
  'deli',
  'meal_takeaway',
  'meal_delivery',
]);

/** Check if a Places API result is a grocery-type place. */
export function isGroceryPlace(place) {
  const types = place?.types;
  if (Array.isArray(types) && types.length > 0 && types.some((t) => GROCERY_PLACE_TYPES.has(String(t))))
    return true;
  const primary = place?.primaryType;
  return typeof primary === 'string' && GROCERY_PLACE_TYPES.has(primary);
}

/**
 * Extract state, city, ffCity, and postal_code from Google Geocode address_components.
 * - city: the locality (e.g. "Tulsa") — used for display/zip lookup
 * - ffCity: city name used for Furnished Finder URLs. Always uses locality so
 *   the FF page URL matches the actual city (neighborhoods/sublocalies are not
 *   valid FF cities and would 404).
 */
export function parseCityState(addressComponents) {
  let state = '';
  let city = '';
  let postal_code = '';
  if (!Array.isArray(addressComponents)) return { state, city, ffCity: city, postal_code };
  for (const component of addressComponents) {
    const types = component.types || [];
    if (types.includes('administrative_area_level_1'))
      state = (component.short_name || '').trim();
    if (types.includes('locality'))
      city = (component.long_name || component.short_name || '').trim();
    if (types.includes('postal_code'))
      postal_code = (component.long_name || component.short_name || '').trim();
  }
  if (!city) {
    for (const component of addressComponents) {
      if ((component.types || []).includes('sublocality'))
        city = (component.long_name || component.short_name || '').trim();
      if (!city && (component.types || []).includes('administrative_area_level_2'))
        city = (component.long_name || component.short_name || '').trim();
    }
  }
  return { state, city, ffCity: city, postal_code };
}

/**
 * Parse Furnished Finder HTML for listing locations, first photo, and details.
 * Lightweight: regex only on existing html, no extra fetches.
 */
export function parseFFListings(html) {
  if (!html || typeof html !== 'string') return [];
  const list = [];
  const locRe = /\\?"approxLocation\\?"\s*:\s*\{\s*\\?"latitude\\?"\s*:\s*([-\d.]+)\s*,\s*\\?"longitude\\?"\s*:\s*([-\d.]+)/g;
  const photoRe = /\\?"photos\\?"\s*:\s*\[\s*\{\s*\\?"url\\?"\s*:\s*\\?"((https?:\/\/[^"]+))\\?"/g;
  const locations = [];
  let m;
  while ((m = locRe.exec(html)) !== null) {
    locations.push({ index: m.index, lat: parseFloat(m[1], 10), lng: parseFloat(m[2], 10) });
  }
  const photos = [];
  while ((m = photoRe.exec(html)) !== null) {
    photos.push({ index: m.index, url: m[1].replace(/\\\//g, '/') });
  }
  const nameRe = /\\?"name\\?"\s*:\s*\\?"([^"]*)\\?"/;
  const bedRe = /\\?"bedroomCount\\?"\s*:\s*(\d+(?:\.\d+)?)/;
  const bathRe = /\\?"bathroomCount\\?"\s*:\s*(\d+(?:\.\d+)?)/;
  const amountRe = /\\?"amount\\?"\s*:\s*\\?"([^"]*)\\?"/;
  const petsRe = /\\"petsAllowed\\"/;
  for (let i = 0; i < locations.length; i++) {
    const loc = locations[i];
    const listingEnd = locations[i + 1] ? locations[i + 1].index : html.length;
    const listingChunk = html.slice(loc.index, listingEnd);
    const nextPhoto = photos.find((p) => p.index > loc.index);
    const photoUrl = nextPhoto ? nextPhoto.url : null;
    let propertyUrl = null;
    if (photoUrl) {
      const idMatch = photoUrl.match(/furnishedfinder\.com\/(\d+)\//);
      if (idMatch) propertyUrl = `https://www.furnishedfinder.com/property/${idMatch[1]}_1`;
    }
    const nameMatch = listingChunk.match(nameRe);
    const bedMatch = listingChunk.match(bedRe);
    const bathMatch = listingChunk.match(bathRe);
    const amountMatch = listingChunk.match(amountRe);
    const rawName = nameMatch ? nameMatch[1].replace(/\\"/g, '"') : null;
    const rawAmount = amountMatch ? amountMatch[1].replace(/\\"/g, '"') : null;
    const amountNumeric = rawAmount
      ? (() => {
          const digits = rawAmount.replace(/[^0-9.]/g, '');
          const n = Number(digits);
          return Number.isFinite(n) ? n : null;
        })()
      : null;
    const petsAllowed = petsRe.test(listingChunk);
    list.push({
      id: `ff-${loc.lat}-${loc.lng}-${i}`,
      lat: loc.lat,
      lng: loc.lng,
      photoUrl,
      propertyUrl,
      name: rawName ? rawName.replace(/\\/g, '').replace(/\s+/g, ' ').trim() || null : null,
      bedroomCount: bedMatch ? parseFloat(bedMatch[1], 10) : null,
      bathroomCount: bathMatch ? parseFloat(bathMatch[1], 10) : null,
      amount: rawAmount ? rawAmount.replace(/\\/g, '').replace(/\$\$/g, '$').replace(/\s+/g, ' ').trim() || null : null,
      amountNumeric,
      petsAllowed,
    });
  }
  return list;
}
